import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { analyticsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { categoryLabel } from "../constants/categories";
import { formatMoney } from "../pos/utils/money";
import StockValue from "../components/StockValue";
import GetStartedCard from "../components/GetStartedCard";

const StatCard = ({ title, value, subtitle, icon, color = "blue" }) => (
  <div
    className={`bg-white rounded-xl shadow-lg p-6 border-s-4 border-${color}-500`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
        <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
        {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
      </div>
      <div className="text-5xl opacity-20">{icon}</div>
    </div>
  </div>
);

const Dashboard = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    analyticsAPI
      .getDashboard()
      .then((response) => setStats(response.data))
      .catch((err) =>
        setError(err.response?.data?.error || t("dashboard.loadFailed")),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GetStartedCard />

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
          {t("dashboard.title")}
        </h1>
        <p className="text-gray-600">{t("dashboard.welcome")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t("dashboard.totalProducts")}
          value={stats?.overview?.total_products || 0}
          icon="📦"
          color="blue"
        />
        <StatCard
          title={t("dashboard.totalCustomers")}
          value={stats?.overview?.total_customers || 0}
          icon="👥"
          color="green"
        />
        <StatCard
          title={t("dashboard.monthlyRevenue")}
          value={formatMoney(stats?.overview?.monthly_revenue || 0)}
          subtitle={t("dashboard.weekly", {
            amount: formatMoney(stats?.overview?.weekly_revenue || 0),
          })}
          icon="💰"
          color="yellow"
        />
        <StatCard
          title={t("dashboard.pendingPreorders")}
          value={stats?.overview?.pending_preorders || 0}
          icon="📅"
          color="purple"
        />
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            {t("dashboard.topProducts")}
          </h2>
          <Link
            to="/admin/products"
            className="shrink-0 text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            {t("dashboard.viewAll")}
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-start py-3 px-4 font-semibold text-gray-700">
                  {t("dashboard.columns.product")}
                </th>
                <th className="text-start py-3 px-4 font-semibold text-gray-700">
                  {t("dashboard.columns.category")}
                </th>
                <th className="text-end py-3 px-4 font-semibold text-gray-700">
                  {t("dashboard.columns.price")}
                </th>
                <th className="text-end py-3 px-4 font-semibold text-gray-700">
                  {t("dashboard.columns.sold")}
                </th>
                <th className="text-end py-3 px-4 font-semibold text-gray-700">
                  {t("dashboard.columns.revenue")}
                </th>
              </tr>
            </thead>
            <tbody>
              {stats?.top_products?.slice(0, 5).map((product) => (
                <tr
                  key={product.id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-800">
                      {product.name}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm whitespace-nowrap">
                      {categoryLabel(product.category)}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-end text-gray-600 whitespace-nowrap">
                    {formatMoney(product.price)}
                  </td>
                  <td className="py-4 px-4 text-end font-semibold text-gray-800">
                    {product.total_sold}
                  </td>
                  <td className="py-4 px-4 text-end font-bold text-green-600 whitespace-nowrap">
                    {formatMoney(product.total_revenue || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {stats?.low_stock_alerts?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between gap-3 mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              {t("dashboard.lowStock")}
            </h2>
            <Link
              to="/admin/products"
              className="shrink-0 text-red-600 hover:text-red-700 font-medium text-sm"
            >
              {t("dashboard.manageStock")}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.low_stock_alerts.slice(0, 6).map((product) => (
              <div
                key={product.id}
                className="border border-red-200 rounded-lg p-4 bg-red-50"
              >
                <h3 className="font-semibold text-gray-800 mb-1">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {categoryLabel(product.category)}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-red-600">
                    {t("dashboard.stock", { count: product.stock_quantity })}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t("dashboard.price", { amount: formatMoney(product.price) })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <StockValue />
    </div>
  );
};

export default Dashboard;
